import { IListener } from "../../../../Global/Base/Events";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Icon } from "../FormUI/FormUI";
import { FormProfileItem } from "../XGControls/Forms/FormProfileItem";
import { FormProfileManager } from "../XGControls/Forms/FormProfileManager";
import { FormProfileDetailBuilder, IProfileData } from "./FormProfileDetailBuilder";
import { FormProfileItemBuilder } from "./FormProfileItemBuilder";
import { IContentBuilder } from "./IContentBuilder";

/**
 * @internal
 */
export class FormProfileBuilder implements IContentBuilder {
    //#region Fields
    private readonly _profileManager: FormProfileManager;
    private readonly _element: HTMLElement;
    /** Zda mají být zobrazeny akce spojené s menu */
    private readonly _showMenuActions: boolean;
    //#endregion Fields

    /**
     * Konstruktor
     * @param profileManager Manažer s profily
     * @param showMenuActions Zda se mají zobrazovat akce spojené s menu
     */
    constructor(profileManager: FormProfileManager, showMenuActions: boolean) {
        this._profileManager = profileManager;
        this._showMenuActions = showMenuActions;

        this._element = createElement({ classes: "form-profile-builder" });
        this._element.appendChild(this._createAddButton());
    }

    //#region Methods
    /** Načte profily z managera a spustí vytvoření jejich prvků */
    private async _loadData(): Promise<void> {
        for (const formProfile of this._profileManager.profiles) {
            await this._createProfileButton(formProfile);
        }
    }

    /** Vytvoří strukturu ke zobrazení */
    public async build(): Promise<HTMLElement> {
        await this._loadData();

        return this._element;
    }

    /**
     * Vytvoří prvek s informacemi o jednom profilu
     * @param profile Jeden konkrétní profil
     */
    private async _createProfileButton(profile: FormProfileItem): Promise<void> {
        const formProfileItem = new FormProfileItemBuilder(this._profileManager, profile);
        formProfileItem.updateMenuActionsVisibility(this._showMenuActions);

        this._element.appendChild(await formProfileItem.build());
    }

    /** Vytvoří tlačítko pro tvorbu nového profilu */
    private _createAddButton(): HTMLElement {
        const element = createElement({ classes: "form-profile-item" });
        const iconElement = createElement({ classes: `icon ${Icon[Icon.new]}` }); //`
        const textElement = createElement({ classes: "text" });
        const titleElement = createElement({ classes: "title" });
        Localization.addLocData(titleElement, "GUI-623219"); // Přidat zobrazení

        textElement.appendChild(titleElement);

        element.appendChild(iconElement);
        element.appendChild(textElement);
        element.addEventListener("click", async () => this._createEditableDetail(element));

        return element;
    }

    /**
     * Vytvoří detail pro nový profil
     * @param element Element tlačítka, který tento detail vytvořil
     */
    private async _createEditableDetail(element: HTMLElement): Promise<void> {
        const removeNamingBox = () => {
            namingBox.remove();
            element.classList.remove("hidden");
        };

        const save = async (profileInfo: IProfileData) => {
            const result = await this._profileManager.saveNewProfile(profileInfo);

            if (result) { // Je-li null, tak byla zobrazena hláška o již existujícím názvu profilu a nebude se dělat nic
                removeNamingBox();

                this._createProfileButton(result);
            }
        };

        this._profileManager.profileEdited.trigger(this._profileManager, null);
        element.classList.add("hidden");

        const namingBox = createElement({ classes: "naming-box-outer" });
        const formProfileDetailBuilder = new FormProfileDetailBuilder({
            profileManager: this._profileManager,
            name: "",
            isDefault: false,
            isBasicProfile: false,
            showInNavigationTree: false,
            queryMode: null,
            showMenuActions: this._showMenuActions,
            saveFunction: save,
            isImplementation: false,
            cancelFunction: () => {
                removeNamingBox();

                this._profileManager.reloadActualProfile();
            },
            storeFilters: true,
        });
        namingBox.appendChild(await formProfileDetailBuilder.build());

        let editListener: IListener;

        this._profileManager.profileEdited.add(editListener = () => {
            removeNamingBox();

            this._profileManager.profileEdited.remove(editListener);
        });

        element.insertAdjacentElement("afterend", namingBox);
    }
    //#endregion Methods
}
